"use client"

import { useState, useRef, useEffect } from "react"
import {
  Share2, Link2, Check, Mail, Linkedin, Download,
  X as XIcon, Smartphone,
} from "lucide-react"

type Props = {
  url: string
  title: string
  /**
   * Suggested caption used for the Instagram-style "download image + copy caption"
   * flow. Falls back to the title when not provided.
   */
  caption?: string
  /**
   * Public URL of the page's OG image. When provided, enables a "Save share card"
   * option that downloads the OG card so the user can post it to Instagram, where
   * web-share URL flows are not supported.
   */
  ogImageUrl?: string
}

export function ShareButton({ url, title, caption, ogImageUrl }: Props) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [downloadStatus, setDownloadStatus] = useState<"idle" | "working" | "done">("idle")
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [open])

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard blocked — silent fail
    }
  }

  async function nativeShare() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, url })
      } catch {
        // user cancelled
      }
    }
    setOpen(false)
  }

  // Open social share URLs in a sized popup window. Bypasses the platform's
  // app/universal-link interception (which would otherwise just open LinkedIn's
  // home feed or X's timeline instead of the actual share modal). On mobile
  // where popups aren't available, falls back to a regular new-tab navigation.
  function openShareWindow(url: string, e?: React.MouseEvent) {
    if (e) e.preventDefault()
    setOpen(false)
    if (typeof window === "undefined") return
    const w = 600
    const h = 600
    const left = (window.screen.width - w) / 2
    const top = (window.screen.height - h) / 2
    const popup = window.open(
      url,
      "rmar-share",
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no`
    )
    // Popup blocked (common on mobile) — fall back to a normal navigation.
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      window.open(url, "_blank", "noopener,noreferrer")
    }
  }

  // Instagram doesn't accept web-share URLs for posts, so the workable flow is:
  // download the page's OG card image + copy a caption with the URL, then the
  // user opens Instagram and posts manually.
  async function saveImageForInstagram() {
    if (!ogImageUrl) return
    setDownloadStatus("working")
    try {
      const captionText = `${caption ?? title}\n\n${url}`
      // Try to copy caption to clipboard first — this is the fragile part
      try { await navigator.clipboard.writeText(captionText) } catch {}

      // Fetch the OG image and trigger a download
      const res = await fetch(ogImageUrl)
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = objectUrl
      a.download = `rmar-share-${url.split("/").pop() || "share"}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)

      setDownloadStatus("done")
      setTimeout(() => setDownloadStatus("idle"), 3000)
    } catch {
      setDownloadStatus("idle")
    }
  }

  const encodedUrl = encodeURIComponent(url)
  const encodedTitle = encodeURIComponent(title)
  const encodedShare = encodeURIComponent(`${title}\n\n${url}`)

  const isAppleDevice =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent)
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share

  const itemClass =
    "flex items-center gap-2.5 w-full px-3.5 py-2 text-sm hover:bg-[var(--secondary)] transition-colors text-left"
  const iconClass = "w-3.5 h-3.5 shrink-0"

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
        aria-label="Share"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 size={14} />
        <span>Share</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-8 z-50 w-64 bg-white border border-[var(--border)] rounded-xl shadow-lg py-1.5 animate-in fade-in slide-in-from-top-1 duration-150"
          role="menu"
        >
          <button onClick={copyLink} className={itemClass} role="menuitem">
            {copied ? <Check size={14} className="text-[var(--accent)] shrink-0" /> : <Link2 size={14} className="shrink-0" />}
            {copied ? "Copied!" : "Copy link"}
          </button>

          {ogImageUrl && (
            <button onClick={saveImageForInstagram} className={itemClass} role="menuitem">
              {downloadStatus === "done" ? (
                <Check size={14} className="text-[var(--accent)] shrink-0" />
              ) : (
                <Download size={14} className="shrink-0" />
              )}
              {downloadStatus === "working"
                ? "Preparing image…"
                : downloadStatus === "done"
                  ? "Image saved · caption copied"
                  : "Save image for Instagram"}
            </button>
          )}

          <div className="my-1 mx-3.5 h-px bg-[var(--border)]" />

          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClass}
            role="menuitem"
            onClick={(e) => openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, e)}
          >
            <Linkedin size={14} className="shrink-0" />
            LinkedIn
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClass}
            role="menuitem"
            onClick={(e) => openShareWindow(`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`, e)}
          >
            <XIcon size={14} className="shrink-0" />
            Post on X
          </a>
          <a
            href={`https://www.threads.net/intent/post?text=${encodedShare}`}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClass}
            role="menuitem"
            onClick={(e) => openShareWindow(`https://www.threads.net/intent/post?text=${encodedShare}`, e)}
          >
            {/* Threads — inline SVG (no lucide icon yet) */}
            <svg viewBox="0 0 24 24" fill="currentColor" className={iconClass}>
              <path d="M17.7 11.4c-.1 0-.2-.1-.2-.1-.2-3.5-2.1-5.5-5.3-5.5-1.9 0-3.5.8-4.5 2.3l1.7 1.2c.7-1.1 1.8-1.4 2.7-1.4 1.2 0 2.1.3 2.6 1 .4.5.6 1.1.7 1.9-.9-.2-1.9-.3-3-.3-3 .2-4.9 1.9-4.8 4.3.1 1.2.7 2.3 1.7 3 .9.6 2 .9 3.2.9 1.6-.1 2.8-.7 3.7-1.8.6-.8 1.1-1.9 1.2-3.3 1 .6 1.8 1.5 2.2 2.5.7 1.7.7 4.5-1.6 6.8-2 2-4.5 2.9-7.4 2.9-3.3 0-5.7-1.1-7.3-3.1C2 19 1.1 16.2 1 12c0-4.1.9-7 2.6-8.9C5.2 1.1 7.6 0 10.9 0c3.3 0 5.8 1 7.4 3.1 1.4 1.8 2.2 4.2 2.3 7.3 0 .1-.1.2-.1.3-.1.3-.4.5-.7.5-.4 0-.7-.2-.7-.5-.1-2.6-.7-4.6-1.8-6-1.3-1.7-3.3-2.5-6.1-2.5-2.8 0-4.8.8-6.1 2.4C3.8 6.2 3 8.5 3 12c0 3.5.8 5.8 2.4 7.6 1.4 1.6 3.3 2.5 6.1 2.5 2.6 0 4.7-.7 6.4-2.3 1.8-1.8 1.9-3.9 1.3-5.4-.4-.9-1-1.6-1.7-2.1-.3 1.6-1 2.9-2 3.7-1 .9-2.4 1.4-4 1.5-1.6.1-3-.3-4.1-1.1-1.3-1-2.1-2.4-2.1-4 0-3 2.2-5.1 5.9-5.4 1.3-.1 2.5 0 3.5.2-.2-1.1-.5-1.9-.9-2.4-.7-.9-1.7-1.3-3.1-1.3-1.2 0-2.2.3-2.9 1l-1.6-1.2c1.2-1.8 3-2.7 5.2-2.7 3.7 0 5.9 2.4 6.1 6.4.7.4 1.3.9 1.8 1.4z"/>
            </svg>
            Threads
          </a>
          <a
            href={`https://bsky.app/intent/compose?text=${encodedShare}`}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClass}
            role="menuitem"
            onClick={(e) => openShareWindow(`https://bsky.app/intent/compose?text=${encodedShare}`, e)}
          >
            {/* Bluesky — inline SVG */}
            <svg viewBox="0 0 600 530" fill="currentColor" className={iconClass}>
              <path d="M135.7 44.4C211.2 101.1 292.4 215.9 322 277.5c29.6-61.6 110.8-176.4 186.3-233.1C562.7 3.6 651-32.6 651 67.7c0 20-11.5 168.1-18.2 192.2-23.5 83.7-108.8 105-184.7 92.1 132.7 22.6 166.4 97.4 93.5 172.2C403.7 666.4 343.2 489.4 327.7 444c-2.9-8.3-4.2-12.2-5.7-8.9-1.5-3.3-2.8.6-5.7 8.9-15.5 45.4-76 222.4-214 80.2-72.8-74.8-39.1-149.6 93.5-172.2-75.8 12.9-161.2-8.4-184.7-92.1C4.5 235.8-7 87.7-7 67.7c0-100.3 88.3-64.1 142.7-23.3z"/>
            </svg>
            Bluesky
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClass}
            role="menuitem"
            onClick={(e) => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, e)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className={iconClass}>
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Facebook
          </a>
          <a
            href={`mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`Check this out:\n\n${url}`)}`}
            className={itemClass}
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <Mail size={14} className="shrink-0" />
            Email
          </a>

          {canNativeShare && (
            <>
              <div className="my-1 mx-3.5 h-px bg-[var(--border)]" />
              <button onClick={nativeShare} className={itemClass} role="menuitem">
                <Smartphone size={14} className="shrink-0" />
                {isAppleDevice ? "More (iOS share sheet)" : "More options…"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
